import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { Branch, Company, Invitation } from 'app/models';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, first, map, retry, takeUntil } from 'rxjs/operators';

import { CompanyService } from 'app/services/company-service/company.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarType } from 'app/constants/Enums';
import { SnackBarService } from '../snackbar-service/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { Params } from '@angular/router';
import { ApiUrl } from 'app/constants/ApiUrls';
import { AuthService } from '../auth.service';

@Injectable()
export class InvitationsService implements OnDestroy {
  readonly invitationsUrl = `${environment.apiUrl}/api/v2/Invites`;

  private _invitations: BehaviorSubject<Invitation[]> = new BehaviorSubject<Invitation[]>(null);
  public invitations$: Observable<any> = this._invitations.asObservable();

  private _recentInvitations: BehaviorSubject<Invitation[]> = new BehaviorSubject<Invitation[]>(null);
  public recentInvitations$: Observable<any> = this._recentInvitations.asObservable();

  private onDestroy$ = new Subject<void>();
  private _company: Company;
  public loading:boolean = false;
  private user;

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    private _companyService: CompanyService,
    public snackBar: MatSnackBar,
    public _snackbarService: SnackBarService,
    public readonly authService: AuthService
  ) {
    this._companyService.company$.pipe(takeUntil(this.onDestroy$)).subscribe(
      (company) => (this._company = company),
      (error) =>
        console.error(
          '[invitations-service] COMPANY SUBSCRIPTION ERROR: ',
          error
        )
    );

    this.authService.user$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (user) => (this.user = user),
        error: (error) =>
          console.error('[invitations-service] USER SUBSCRIPTION ERROR: ', error),
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  private createInvitation(
    email: string,
    branchAssignments: Array<Branch>,
    apps: Array<any>,
    isAdmin: boolean,
    directToBranch: boolean
  ): Invitation {
    const invitation = new Invitation();
    this._translateService
      .get('application_portal')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (invitation.appName = value));
    this._translateService
      .get('application_invite')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (invitation.userMessage = value));
    invitation.email = email;
    invitation.branchAssignments = branchAssignments;

    if (this.user.isInternal) {
      invitation.inviteFrom = 'Allegion';
    } else if (this.user) {
      invitation.inviteFrom = this.user.name;
    }

    if (directToBranch) {
      const branch = branchAssignments[0];
      const isBranch = this._company.branches.find(
        (companyBranches) => companyBranches.accountId === branch.accountId
      );
      if (isBranch) {
        invitation.companyAccountId = branch.accountId;
        invitation.companyName = branch.name;
      }
    } else if (this._company) {
      invitation.companyAccountId = this._company.accountId;
      invitation.companyName = this._company.name;
    }

    // Format the apps
    const appsFormatted: any = apps.map((app) => ({
        appId: app.clientId,
        roles: app.roles
          .filter((role) => role.hasAccess)
          .map((role) => role.name),
      }));

    invitation.apps = appsFormatted || [];
    invitation.isCompanyManager = isAdmin;
    invitation.signupUrl = environment.signupUrl;
    invitation.email = invitation.email.toLowerCase();
    invitation.companyId = this._company.companyId;
    return invitation;
  }

  sendInvitation(
    email: string,
    branchAssignments: Array<Branch>,
    apps: Array<any>,
    isAdmin = false,
    directToBranch = false
  ): Observable<any> {
    const invitation = this.createInvitation(
      email,
      branchAssignments,
      apps,
      isAdmin,
      directToBranch
    );

    return this._http
      .post(ApiUrl.invites, invitation, { observe: 'response' })
      .pipe(
        first(),
        map((response) => {
          let successMsg: string;
          // Check if the invite was created or processed

          let success = '';
          this._translateService
            .get('SNACKBAR.success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((value) => (success = value));
          if (response.status === 200) {
            this._translateService
              .get('INVITATIONS_SERVICE.exists_and_added')
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((value) => (successMsg = value));
            this._snackbarService.showSnackbar(
              SnackbarType.success,
              success,
              successMsg
            );
          } else if (response.status === 201) {
            this._translateService
              .get('USERS_SERVICE.invite_success')
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((value) => (successMsg = value));
            this._snackbarService.showSnackbar(
              SnackbarType.success,
              success,
              successMsg
            );
          }
          return of(true);
        }),
        catchError((error) => {
          this._handleCreateInvitationError(error);
          return of(false);
        })
      );
  }

  private _handleCreateInvitationError(
    error: HttpErrorResponse
  ) {
    let errorMsg = '';
    let errorTitle = '';
    let genericErrorMsg = "";
    this._translateService
    .get('ERROR_TRY_LATER')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (genericErrorMsg = value));
    this._translateService
      .get('SNACKBAR.error')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorTitle = value));
    this._translateService
      .get('SNACKBAR.error_msg')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorMsg = value));

    if (error.status === 400) {
      errorMsg = error.error.detail;
      genericErrorMsg = error.error.detail;
    }
    this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, genericErrorMsg);
    this._invitations.error(errorMsg);
  }

  getRecentInvitations(email?: string): void {
    let suffixParam = '';
    if (email) {
      suffixParam = `?email=${email}`;
    }
    this._http.get(`${this.invitationsUrl}/Recent${suffixParam}`).subscribe(
      (data: Invitation[]) => {
        data.forEach(i => {
          i.dateInvited = new Date(i.dateInvited).toLocaleString();
        })
        this._recentInvitations.next(data);
      },
      (error) => this._handleInvitationsError(error)
    );
  }

  getCompanyInvitations(accountId: string, email?: string): void {
    let url = '';
    if (accountId && email) {
      url = `${this.invitationsUrl}?companyId=${accountId}&email=${email}`;
    } else if (accountId) {
      url = `${ApiUrl.invites}?companyId=${accountId}`;
    } else if (email) {
      url = `${this.invitationsUrl}?email=${email}`;
    }
    this._http.get(url).subscribe(
      (data: Invitation[]) => {
        this._invitations.next(data);
      },
      (error) => this._handleInvitationsError(error)
    );
  }

  private _handleInvitationsError(error: HttpErrorResponse): void {
    let errorMsg: string;
    this.loading = false;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[invitations-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[invitations-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    this._snackbarService.showSnackbar(SnackbarType.error, 'Error', errorMsg);
    this._invitations.error(errorMsg);
  }

  resendInvitation(emails: string[]): void {
    this.loading = true;
    
    emails.forEach((email, i) => {
      this._http
        .post(ApiUrl.resendinvites,{email: email})
        .pipe(retry(2))
        .subscribe(
          (data: Invitation) => {
            if (i === emails.length - 1) {
              let successMsg: string;
              let success = '';
              this._translateService
                .get('SNACKBAR.success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (success = value));
              this._translateService
                .get('INVITATIONS_SERVICE.resend_success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (successMsg = value));
                this.loading =false;
              this._snackbarService.showSnackbar(
                SnackbarType.success,
                success,
                successMsg
              );
            }
          },
          (error) => this._handleInvitationsError(error)
        );
    });
  }

  deleteInvitations(emails: string[]): void {
    this.loading = true;
    emails.forEach((email, i) => {
      let params : Params = new HttpParams;
      params = params.append('email', email);
      this._http
        .delete(ApiUrl.invites, {params: params})
        .pipe(retry(2))
        .subscribe(
          (data: Invitation) => {
            if (i === emails.length - 1) {
              let successMsg: string;
              this._translateService
                .get('INVITATIONS_SERVICE.deleting')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (successMsg = value));
                let success = '';
              this._translateService
                .get('SNACKBAR.success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (success = value));
                this.loading =false;
              this._snackbarService.showSnackbar(
                SnackbarType.success,
                success,
                successMsg
              );
              this.getCompanyInvitations(this._company.companyId);
            }
          },
          (error) => this._handleInvitationsError(error)
        );
    });
  }

  deleteInvitation(email: string): void {
    let params : Params = new HttpParams;
    params = params.append('email', email);
    this._http
      .delete(this.invitationsUrl, {params: params})
      .pipe(retry(2))
      .subscribe(
        (data: Invitation) => {
          let successMsg: string;
          this._translateService
            .get('INVITATIONS_SERVICE.deleting')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((value) => (successMsg = value));
          this.snackBar.dismiss();
          this.snackBar.open(successMsg, null, { duration: 2000 });
          this.getCompanyInvitations(this._company.companyId);
        },
        (error) => this._handleInvitationsError(error)
      );
  }
}
